import apiSlice from '../api/api.slice';
import { IFeedItem } from '../feed/feed.d';
import {
  IIPostWithUserAndAttachmentsRequestParams,
  IPost,
  IPostAttachment,
  IPostDataUploadRequest,
  IPostDetailResponse,
  IPostDraft,
  IPostWithUserAndAttachments,
} from './post';
import {
  IGetProfileFeedArgs,
  IPaginatedFeedResponse,
} from '@/redux/feed/feed.d';
import {
  POSTS_PAGE_LIMIT,
  compactParams,
  normalizeProfileFeedResponse,
  serializeProfileFeedArgs,
  mergeProfileFeed,
  profileFeedForceRefetch,
} from './post.utils';

const postApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPosts: builder.query<
      IPostWithUserAndAttachments[],
      IIPostWithUserAndAttachmentsRequestParams
    >({
      query: (params) => ({
        url: '/api/v1/post',
        method: 'GET',
        params,
      }),
      transformResponse: (response: { posts: IPostWithUserAndAttachments[] }) =>
        response.posts,
    }),
    getPostById: builder.query<IPostDetailResponse, string>({
      query: (id) => ({
        url: `/api/v1/post/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { post: IPostDetailResponse }) =>
        response.post,
    }),
    getSearchAutoComplete: builder.query<
      { suggestions: string[]; posts: { _id: string; title?: string }[] },
      { searchQuery: string; searchMode?: 'title' | 'user' }
    >({
      query: (params) => ({
        url: `/api/v1/post/search/autoComplete`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: {
        suggestions: string[];
        posts: { _id: string; title?: string }[];
      }) => ({
        suggestions: response.suggestions,
        posts: response.posts ?? [],
      }),
    }),
    getPostByIdAndApiKey: builder.query<
      IPostDetailResponse,
      { postId: string; token: string }
    >({
      query: ({ postId, token }) => ({
        url: `/api/v1/post/preview/postDetails/?postId=${postId}&token=${token}`,
        method: 'GET',
      }),
      transformResponse: (response: { post: IPostDetailResponse }) =>
        response.post,
    }),
    getPostByUserId: builder.query<IPostWithUserAndAttachments[], string>({
      query: (id) => ({
        url: `/api/v1/post/getPostsByUserId/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { posts: IPostWithUserAndAttachments[] }) =>
        response.posts,
    }),
    /**
     * Unified profile feed: a user's posts AND project groups, interleaved and
     * keyset-paginated server-side in one DB query.
     *
     * Pages ACCUMULATE into a single cached list:
     * - `serializeQueryArgs` keys the cache by username+search+sort+visibility
     *   (NOT cursor), so changing search/sort/status-tab starts a fresh first
     *   page automatically.
     * - `merge` appends new `items` onto the existing list and carries the
     *   latest `nextCursor`/`hasMore`.
     * - `forceRefetch` re-runs the query whenever the cursor changes so each
     *   "load more" actually hits the network.
     *
     * Items are a discriminated union (`type: 'post' | 'projectGroup'`).
     */
    getProfileFeed: builder.query<IPaginatedFeedResponse, IGetProfileFeedArgs>({
      query: ({
        username,
        cursor,
        limit = POSTS_PAGE_LIMIT,
        search,
        sort,
        visibility,
        category,
      }) => ({
        url: `/api/v1/post/getProfileFeed/${username}`,
        method: 'GET',
        // compactParams drops any absent filter so we never send blank params.
        params: compactParams({
          limit,
          cursor,
          search,
          sort,
          visibility,
          category,
        }),
      }),
      transformResponse: normalizeProfileFeedResponse,
      serializeQueryArgs: serializeProfileFeedArgs,
      merge: mergeProfileFeed,
      forceRefetch: profileFeedForceRefetch,
    }),
    createPost: builder.mutation<
      {
        post: IPost;
        attachments: IPostAttachment[];
      },
      FormData
    >({
      query: (data) => ({
        url: '/api/v1/post/create',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: {
        post: {
          post: IPost;
          attachments: IPostAttachment[];
        };
      }) => response.post,
    }),
    createDraftPost: builder.mutation<IPostDraft, FormData>({
      query: (body) => ({
        url: '/api/v1/post/createPost',
        method: 'POST',
        body,
      }),
      transformResponse: (response: {
        post: IPostDraft;
        attachments: IPostAttachment;
      }) => response.post,
    }),
    updatePostData: builder.mutation<
      Partial<IPost>,
      { postId: string; data: IPostDataUploadRequest }
    >({
      query: ({ postId, data }) => ({
        url: `/api/v1/post/updatePost/${postId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (response: { post: Partial<IPost> }) => response.post,
    }),
    updatePostAttachment: builder.mutation<
      IPostAttachment[],
      { postId: string; currentPage: number; data: FormData }
    >({
      query: ({ postId, currentPage, data }) => ({
        url: `/api/v1/post/updateAttachments/${postId}`,
        method: 'PUT',
        body: data,
        params: {
          currentPage,
        },
      }),
      transformResponse: (response: { attachments: IPostAttachment[] }) =>
        response.attachments,
    }),
    deletePostDelete: builder.mutation<undefined, string>({
      query: (postId) => ({
        url: `/api/v1/post/delete/${postId}`,
        method: 'DELETE',
      }),
    }),
    // Mixed posts + admin-featured project groups, interleaved by featuredOrder.
    getFeaturedPosts: builder.query<IFeedItem[], { limit?: number }>({
      query: (params) => ({
        url: '/api/v1/post/featured',
        method: 'GET',
        params,
      }),
      transformResponse: (response: { posts: IFeedItem[] }) => response.posts,
    }),
  }),
});

apiSlice.enhanceEndpoints({
  addTagTypes: ['Post', 'UserPosts'],
  endpoints: {
    getPosts: {
      providesTags: ['Post'],
    },
    getPostById: {
      providesTags: ['Post'],
    },
    getPostByUserId: {
      providesTags: ['UserPosts'],
    },
    getProfileFeed: {
      providesTags: ['UserPosts'],
    },
    createPost: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    deletePostDelete: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    updatePostAttachment: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    updatePostData: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    createDraftPost: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
  },
});

export const {
  useCreatePostMutation,
  useGetPostsQuery,
  useLazyGetPostsQuery,
  useGetPostByIdQuery,
  useGetPostByUserIdQuery,
  useGetPostByIdAndApiKeyQuery,
  useUpdatePostAttachmentMutation,
  useUpdatePostDataMutation,
  useCreateDraftPostMutation,
  useLazyGetPostByIdQuery,
  useDeletePostDeleteMutation,
  useLazyGetSearchAutoCompleteQuery,
  useGetProfileFeedQuery,
  useGetFeaturedPostsQuery,
} = postApiSlice;
